import React, { ComponentProps, FC, MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import useClassNames from '@/helpers/hooks/useClassNames';
import Typography from '../typography';
import { classnames } from '@/helpers/utils/classnames';

export interface InputProps extends Omit<ComponentProps<'input'>, 'onChange' | 'key'> {
  label?: string;
  labelDesc?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  variant?: 'primary' | 'secondary';
  labelPosition?: 'top' | 'inline';
  innerRef?: MutableRefObject<HTMLInputElement>;
  error?: string;
  errorMessage?: string;
  isValid?: boolean;
  hideCheckIcon?: boolean;
  validation?: (valueToValidate: string) => boolean;
  renderEndIcon?: () => React.ReactNode;
  required?: boolean;
  placeholder?: string;
  errorClassName?:string;
}

const Input: FC<InputProps> = ({
  label,
  labelDesc,
  onChange,
  onBlur,
  onFocus,
  variant = 'primary',
  labelPosition = 'top',
  className = '',
  innerRef,
  value,
  errorMessage,
  validation,
  children,
  hideCheckIcon,
  isValid: isValidProp,
  required,
  placeholder,
  type = 'text', 
  errorClassName,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [isErrored, setIsErrored] = useState(false);
  const [isEdited, setIsEdited] = useState(false);

  useEffect(() => {
    if (validation) {
      const shouldBeValidated = isTouched && isEdited;
      const validated = validation?.(value as string);
      setIsValid(shouldBeValidated && !!validated);
      setIsErrored(shouldBeValidated && !validated);
    }
  }, [isEdited, isTouched, validation, value]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);
      setIsEdited(true);
    },
    [onChange],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      setIsTouched(true);
      onBlur?.(e);
    },
    [onBlur],
  );

  const bgClassName = useMemo(
    () =>
      ({
        primary: 'bg-white',
        secondary: 'bg-neutral-200',
      }[variant]),
    [variant],
  );

  const isInActiveState = useMemo(() => isFocused || !!value, [isFocused, value]);

  const labelClassName = useClassNames([
    labelPosition ? 'leading-loose' : 'leading-normal',
    labelPosition === 'top' ? 'text-16' : 'text-10',
    'font-medium',
    { ['text-secondary-black']: labelPosition === 'top' },
    { [isInActiveState && label ? 'opacity-1 scale-100' : 'scale-0 opacity-0']: labelPosition === 'inline' },
    {
      ['absolute top-1.5 left-3 font-medium block transition duration-150 ease-out']: labelPosition === 'inline',
    },
  ]);

  const labelContainerClassName = useClassNames([
    {
      'mb-8': labelPosition === 'top' && type !== 'checkbox', // Only add 'mb-8' if type is not 'checkbox'
    },
  ]);

  const inputClassName = useClassNames([
    'relative h-10 focus:border-gray-500 focus:ring-0 w-full rounded-sm  px-3 text-primary-black placeholder:text-sm placeholder:leading-normal placeholder:text-secondary-black focus:outline-none disabled:cursor-not-allowed disabled:bg-neutral-400',
    bgClassName,
    isInActiveState && label && labelPosition === 'inline' ? 'pt-5 pb-1' : 'py-2.5',
    {
      'border-red-500 focus:border-red-500':
        (!!props.error && isInActiveState) ||
        isErrored ||
        (typeof isValidProp !== 'undefined' && !isValidProp && isTouched),
    },
    { 'border-green-500 focus:border-green-500': (!!isValidProp && isInActiveState) || isValid },
    className,
  ]);

  return (
    <div className="relative">
      {/* Only render the label container if it's not a checkbox */}
      {type !== 'checkbox' && (
        <div className={`${label || labelDesc ? labelContainerClassName : ''}`}>
          {label && (
            <span className={labelClassName}>
              {label}
              <span className="!text-red-500">{required && ' *'}</span>
            </span>
          )}
          {labelDesc && (
            <Typography as="label" className={labelClassName}>
              {` (${labelDesc})`}
            </Typography>
          )}
        </div>
      )}
      <div className="">
        <input
          type={type}
          className={inputClassName}
          onChange={handleChange}
          onBlur={handleBlur}
          ref={innerRef}
          value={value}
          placeholder={placeholder}
          {...props}
        />
        {(isValid || (isValidProp && !isInActiveState)) && !isFocused && !hideCheckIcon && (
          <CheckIcon className="absolute right-[3px] top-1/2 h-[4px] w-[4px] -translate-y-1/2 text-green-500" />
        )}
        {children}
      </div>
      {(isErrored || props.error) && (
        <Typography className={classnames(`mt-[12px] text-[12px] font-medium leading-[16px] text-red-500 ${errorClassName??''}`)} as="span">
          {props.error ?? errorMessage}
        </Typography>
      )}
    </div>
  );
};

export default Input;
